import { Play, Pause, Square, Rewind, FastForward } from 'lucide-react';
import { useState, useRef, useEffect, useMemo } from 'react';

export function MediaPlayer({
  isPlaying,
  setIsPlaying,
  speed,
  setSpeed,
  currentTime,
  setCurrentTime,
  minTime,
  maxTime
}: any) {
  const speeds = [0.2, 0.5, 1, 2, 3, 5, 10, 20];
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [showHourMarkers, setShowHourMarkers] = useState(() => {
    try {
      const saved = localStorage.getItem('mediaplayer_hour_markers');
      return saved !== null ? saved === 'true' : true;
    } catch { return true; }
  });
  const [tooltipMark, setTooltipMark] = useState<{ label: string; pos: number } | null>(null);
  const speedMenuRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (speedMenuRef.current && !speedMenuRef.current.contains(e.target as Node)) setShowSpeedMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Persist hour markers toggle to localStorage
  useEffect(() => {
    try { localStorage.setItem('mediaplayer_hour_markers', String(showHourMarkers)); } catch {}
  }, [showHourMarkers]);

  const [localTime, setLocalTime] = useState(currentTime);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    if (!isDraggingRef.current) {
      setLocalTime(currentTime);
    }
  }, [currentTime]);

  const throttleTimeoutRef = useRef<any>(null);
  const throttledSetCurrentTime = (val: number) => {
    if (throttleTimeoutRef.current) return;
    throttleTimeoutRef.current = setTimeout(() => {
      setCurrentTime(val);
      throttleTimeoutRef.current = null;
    }, 100);
  };

  const handleRangeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value);
    setLocalTime(val);
    throttledSetCurrentTime(val);
  };

  const handleRelease = (val: number) => {
    isDraggingRef.current = false;
    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = null;
    }
    setCurrentTime(val);
  };

  useEffect(() => {
    return () => {
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, []);

  const formatTime = (seconds: number) => {
    const totalSeconds = seconds + 9 * 3600;
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    
    const sInt = Math.floor(s);
    const sDec = Math.floor((s - sInt) * 10);
    const sFormat = String(sInt).padStart(2, '0') + (sDec > 0 ? `.${sDec}` : '');
    
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${sFormat}`;
  };

  const endTimeStr = formatTime(maxTime);
  const rangeSpan = maxTime - minTime || 1;
  const progress = ((localTime - minTime) / rangeSpan) * 100;

  const lastRewindClickTime = useRef<number>(0);
  const handleRewind = () => {
    const now = Date.now();
    if (now - lastRewindClickTime.current < 300) {
      // Double click: we already subtracted 10s, subtract another 20s to make it -30s total
      setCurrentTime((prev: number) => Math.max(minTime, prev - 20));
      lastRewindClickTime.current = 0; // Reset
    } else {
      // Single click: subtract 10s
      setCurrentTime((prev: number) => Math.max(minTime, prev - 10));
      lastRewindClickTime.current = now;
    }
  };

  const lastForwardClickTime = useRef<number>(0);
  const handleFastForward = () => {
    const now = Date.now();
    if (now - lastForwardClickTime.current < 300) {
      // Double click: we already added 10s, add another 20s to make it +30s total
      setCurrentTime((prev: number) => Math.min(maxTime, prev + 20));
      lastForwardClickTime.current = 0; // Reset
    } else {
      // Single click: add 10s
      setCurrentTime((prev: number) => Math.min(maxTime, prev + 10));
      lastForwardClickTime.current = now;
    }
  };

  const hourMarks = useMemo(() => {
    const marks: { pos: number; label: string; relSec: number }[] = [];
    const firstHour = Math.ceil((minTime + 32400) / 3600);
    for (let h = firstHour; h * 3600 <= maxTime + 32400; h++) {
      const relSec = h * 3600 - 32400;
      const pos = ((relSec - minTime) / rangeSpan) * 100;
      marks.push({ pos, label: `${String(h).padStart(2, '0')}:00:00`, relSec });
    }
    return marks;
  }, [minTime, maxTime, rangeSpan]);

  // Build slider background: progress fill only (hour marks rendered as DOM elements)
  const sliderBackground = useMemo(() => {
    return `linear-gradient(to right, #4b5563 ${progress}%, #1f2937 ${progress}%)`;
  }, [progress]);

  // Show tooltip when cursor is near a mark
  const handleTrackMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!trackRef.current || !showHourMarkers) { setTooltipMark(null); return; }
    const rect = trackRef.current.getBoundingClientRect();
    const padding = 8;
    const pct = (e.clientX - rect.left - padding) / (rect.width - 2 * padding);
    const threshold = 0.018;
    let nearest: typeof tooltipMark = null;
    let minDist = Infinity;
    for (const mark of hourMarks) {
      const dist = Math.abs(pct - mark.pos / 100);
      if (dist < threshold && dist < minDist) { minDist = dist; nearest = mark; }
    }
    setTooltipMark(nearest);
  };

  return (
    <div className="w-full">
      <div
        className="player-container flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 px-2 sm:px-4 py-1.5 w-full sm:h-10"
        onWheel={(e) => {
          e.preventDefault();
          const delta = e.deltaY > 0 ? -speed : speed;
          setCurrentTime((prev: number) => Math.max(minTime, Math.min(maxTime, prev + delta)));
        }}
      >
        <style>{`
          input[type='range']::-webkit-slider-thumb {
            -webkit-appearance: none; appearance: none;
            width: 8px; height: 8px; background: #9ca3af;
            cursor: pointer; border-radius: 50%; border: 1px solid #4b5563;
            margin-top: -3px; transition: all 0.15s ease-in-out;
          }
          .player-container:hover input[type='range']::-webkit-slider-thumb {
            background: #fff; transform: scale(1.2); box-shadow: 0 0 8px rgba(255,255,255,0.3);
          }
          input[type='range']::-webkit-slider-thumb:active { background:#fff; transform:scale(1.3); }
          input[type='range']::-moz-range-thumb {
            width:8px; height:8px; background:#9ca3af; cursor:pointer;
            border-radius:50%; border:1px solid #4b5563; transition:all 0.15s ease-in-out;
          }
          .player-container:hover input[type='range']::-moz-range-thumb { background:#fff; transform:scale(1.2); }
        `}</style>

        <div className="flex w-full items-center justify-between sm:w-auto sm:justify-start gap-2 sm:gap-3">
          {/* Playback Controls */}
          <div className="flex items-center gap-2 sm:gap-3" style={{ color: '#f97316' }}>
            <Rewind size={14} fill="currentColor" className="cursor-pointer hover:brightness-125 transition-all"
              onClick={handleRewind} title="Back 10s (Double click: 30s)" />
            <Square size={12} fill="currentColor" className="cursor-pointer hover:brightness-125 transition-all"
              onClick={() => { setIsPlaying(false); setCurrentTime(Math.max(minTime, -1)); }} />
            <div onClick={() => setIsPlaying(!isPlaying)} className="cursor-pointer hover:brightness-125 transition-all">
              {isPlaying ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
            </div>
            <FastForward size={14} fill="currentColor" className="cursor-pointer hover:brightness-125 transition-all"
              onClick={handleFastForward} title="Forward 10s (Double click: 30s)" />
            <span className="text-[12px] sm:text-[13px] text-gray-400 select-none tabular-nums">{formatTime(localTime)}</span>
          </div>

          {/* Action Controls (Mobile only) */}
          <div className="flex sm:hidden items-center gap-2 text-gray-500">
            <span className="text-[12px] text-gray-400 select-none tabular-nums">{endTimeStr}</span>
            <div ref={speedMenuRef} className="relative flex items-center">
              <button onClick={() => setShowSpeedMenu(v => !v)}
                className="text-[12px] font-bold text-gray-400 hover:text-white transition-colors w-[30px] p-0 leading-none flex items-center justify-end">
                {speed}x
              </button>
            </div>
          </div>
        </div>

        {/* Progress Bar with DOM hour mark lines */}
        <div
          ref={trackRef}
          className="flex-1 w-full relative flex items-center px-1 sm:px-2 py-2 sm:py-0"
          onMouseMove={handleTrackMouseMove}
          onMouseLeave={() => setTooltipMark(null)}
        >
          <input
            type="range" min={minTime} max={maxTime} step="1" value={localTime}
            onMouseDown={() => { isDraggingRef.current = true; }}
            onTouchStart={() => { isDraggingRef.current = true; }}
            onChange={handleRangeChange}
            onMouseUp={(e) => handleRelease(parseInt(e.currentTarget.value))}
            onTouchEnd={(e) => handleRelease(parseInt(e.currentTarget.value))}
            className="w-full h-[2px] appearance-none cursor-pointer outline-none"
            style={{ background: sliderBackground }}
          />
          {/* Hour mark lines */}
          {showHourMarkers && hourMarks.map((mark, i) => (
            <div
              key={i}
              className="absolute pointer-events-none z-20"
              style={{
                left: `calc(8px + ${mark.pos / 100} * (100% - 16px))`,
                top: '50%',
                transform: 'translateX(-50%) translateY(-50%)',
                width: '2px',
                height: '10px',
                backgroundColor: '#f97316',
              }}
            />
          ))}
          {/* Tooltip for hovered hour mark */}
          {tooltipMark && (
            <div
              className="absolute pointer-events-none z-20 bg-gray-900 border border-gray-600 text-white text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap shadow-lg"
              style={{
                left: `calc(8px + ${tooltipMark.pos / 100} * (100% - 16px))`,
                bottom: 'calc(50% + 8px)',
                transform: 'translateX(-50%)',
              }}
            >
              {tooltipMark.label}
            </div>
          )}
        </div>

        {/* Action Controls (Desktop only) */}
        <div className="hidden sm:flex items-center gap-3 text-gray-500">
          <span className="text-[13px] text-gray-400 select-none tabular-nums">{endTimeStr}</span>

          {/* Speed picker */}
          <div ref={speedMenuRef} className="relative flex items-center">
            <button onClick={() => setShowSpeedMenu(v => !v)}
              className="text-[13px] font-bold text-gray-400 hover:text-white transition-colors w-[36px] p-0 leading-none flex items-center justify-center">
              {speed}x
            </button>
            {showSpeedMenu && (
              <div className="absolute top-full right-1/2 translate-x-1/2 mt-2 bg-[#111827] border border-gray-700 rounded-md shadow-2xl z-[9999] overflow-hidden">
                {[...speeds].reverse().map(s => (
                  <button key={s} onClick={() => { setSpeed(s); setShowSpeedMenu(false); }}
                    className={`w-full text-center px-4 py-[3px] text-[13px] font-bold transition-colors block whitespace-nowrap
                      ${s === speed ? 'text-white bg-blue-600' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}>
                    {s}x
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


